import { FunctionComponent } from "react";

import { DefaultButton, Dialog, Stack, Text, useTheme } from "@bps/fluent-ui";
import { CopyableGuid } from "@components/CopyableText";
import { SectionTitle } from "@components/SectionTitle";
import { Tenant } from "@libs/api/gateways/plt/plt-gateway.dtos";
import { useTransferTenant } from "@libs/api/gateways/plt/plt-gateway.hooks";

interface TransferTenantDialogProps {
  source: Tenant;
  target: Tenant;
  onDismiss: () => void;
  onTransferred: () => void;
}

export const TransferTenantDialog: FunctionComponent<TransferTenantDialogProps> = ({
  source,
  target,
  onDismiss,
  onTransferred
}) => {
  const theme = useTheme();

  const tenantDetails = (label: string, tenant: Tenant) => (
    <Stack tokens={{ childrenGap: 4 }}>
      <Text variant="small">{label}</Text>
      <SectionTitle>{tenant.name}</SectionTitle>
      <CopyableGuid
        value={tenant.id}
        variant="small"
        color={theme.palette.themeSecondary}
      />
    </Stack>
  );

  const apiArgs = {
    sourceId: source.id ?? "",
    destinationId: target.id ?? ""
  };

  const transferTenant = useTransferTenant(apiArgs);
  const confirmTransfer = async () => {
    const confirmed = confirm(
      `Are you sure you want to transfer licences and subscriptions from ${source.name} to ${target.name} and unlink ${source.name}?`
    );
    if (confirmed) {
      await transferTenant.mutateAsync(apiArgs);
      onTransferred();
    }
  };

  return (
    <Dialog
      hidden={false}
      onDismiss={onDismiss}
      dialogContentProps={{
        title: "Transfer licences and subscriptions and unlink tenant",
        showCloseButton: true
      }}
    >
      <Stack tokens={{ childrenGap: 16 }}>
        <Text>
          This will transfer all licences and subscriptions from the source
          tenant to the selected target tenant and unlink the source tenant.
        </Text>
        <Stack horizontal tokens={{ childrenGap: 32 }}>
          {tenantDetails("From", source)}
          {tenantDetails("To", target)}
        </Stack>
        <DefaultButton onClick={confirmTransfer}>
          Confirm transfer and unlink
        </DefaultButton>
      </Stack>
    </Dialog>
  );
};
